import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Step, StepContent, StepLabel, Stepper, Tabs, Tab, Typography, Paper, TextField } from '@material-ui/core';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { AgGridReact } from '@ag-grid-community/react';
import { ValueGetterParams, FirstDataRenderedEvent } from '@ag-grid-community/core';
import '@ag-grid-community/styles/ag-grid.css';
import { get, isEmpty, isNil } from 'lodash';
import { connect } from 'react-redux';

import './_submission-wizard.scss';
import { DropdownOption, PlanMovementConfig, WizardSection } from './submission-wizard.types';
import { AppState, AppThunkDispatch } from 'src/store';
import { API_BASE_URL } from 'src/state/ViewConfig/ViewConfig.types';
import PivotToggle from 'src/components/Mfp/PivotConfigurator/PivotToggle';
import {
  getSelectedOption,
  parsePlanMovementOptionValue,
  setWizardDefaults,
  TabPanel,
  TogglesSelectAll,
  TogglesStatus,
  useGoBack,
  useGoForward,
  WizardActions,
} from './submission-wizard.utils';
import ServiceContainer from 'src/ServiceContainer';
import { usePlanMetadata } from 'src/components/Mfp/PlanMetadataGrid/FetchPlanMetadataHook';
import { formatDate } from 'src/utils/Domain/time.utils';
import { AxiosInstance } from 'axios';
import { clonePlans } from 'src/state/workingSets/workingSets.actions';
import { toast } from 'react-toastify';
import { getMemberNameFromSpace } from 'src/components/Mfp/MfpScopeSelector/MfpScopeUtils';
import { getMfpModule } from 'src/pages/NavigationShell/navigationUtils';
import MfpSubheader from 'src/components/Mfp/MfpSubheader/MfpSubheader';
import { Autocomplete } from '@material-ui/lab';
import TemplateModalWithButton from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

interface SubmissionWizardOwnProps {}
type SubmissionWizardValueProps = ReturnType<typeof mapStateToProps>;
interface SubmissionWizardDispatchProps extends ReturnType<typeof mapDispatchToProps> {}

export type SubmissionWizardProps = SubmissionWizardValueProps &
  SubmissionWizardDispatchProps &
  SubmissionWizardOwnProps;

const mapStateToProps = (state: AppState) => {
  const { controlPoints } = state.viewConfigSlice;
  const { dimensionLabelProperty } = state.settings;
  const locationDisplay = dimensionLabelProperty?.location;
  const currentModule = getMfpModule(state);

  return {
    controlPoints,
    currentModule,
    locationDisplay,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    clonePlans: (payload: { client: AxiosInstance; planIds: number[]; version: string }) =>
      dispatch(clonePlans(payload)),
  };
};

const SubmissionWizard = (props: SubmissionWizardProps) => {
  const { clonePlans, locationDisplay } = props;

  const client = ServiceContainer.axios;
  const logger = ServiceContainer.loggingService;

  const [isModalVisible, setModalVisible] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(0);
  const [planMovement, setPlanMovement] = useState('');
  const [planMovementOptions, setPlanMovementOptions] = useState<DropdownOption[]>([]);
  const [time, setTime] = useState<string | undefined>(undefined);
  const [timeOptions, setTimeOptions] = useState<DropdownOption[]>([]);
  const [allProducts, setAllProducts] = useState(false);
  const [allLocations, setAllLocations] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set<string>());
  const [selectedLocations, setSelectedLocations] = useState(new Set<string>());
  const [summaryTab, setSummaryTab] = useState(0);
  const [{ plans }, setFilters] = usePlanMetadata(client, props.currentModule!);

  const { fromText, toText } = useMemo(() => {
    const parsedPlanMovement = !isEmpty(planMovement) ? planMovement.split(' ') : [];
    const [fromText, _, toText] = parsedPlanMovement;
    return { fromText, toText };
  }, [planMovement]);

  const { toValue, fromValue, products, locations } = useMemo(() => {
    const { option } = getSelectedOption(planMovement, planMovementOptions);
    const [fromValue, _, toValue] = parsePlanMovementOptionValue(option);
    const controlPoint = props.controlPoints ? props.controlPoints[toValue] : null;
    const products = controlPoint?.product || [];
    const locations = controlPoint?.location || [];
    return { toValue, fromValue, products, locations };
  }, [props.controlPoints, planMovement, planMovementOptions]);

  // fetch plan workflows up front
  useEffect(() => {
    const fetchMassWorkflows = async () => {
      const workflowsUrl = `${API_BASE_URL}/silo/${props.currentModule?.siloId}/config/mass-workflows`;
      const response = await client.get<PlanMovementConfig>(workflowsUrl);

      const config: PlanMovementConfig = response.data;
      const planMovementOptions = config.adminWorkflows.map(({ left, right }) => {
        return {
          text: `${left.toUpperCase()} to ${right.toUpperCase()}`,
          value: `${left}-to-${right}`,
        };
      });

      setPlanMovementOptions(planMovementOptions);
    };
    fetchMassWorkflows().catch((e) => {
      toast.error('An error occured fetching the Mass Copy options');
      ServiceContainer.loggingService.error('An error occured fetching the Mass Copy options', e.stack);
    });
  }, [client, props.currentModule]);

  // plan version changed, refetch data (via hook)
  useEffect(() => {
    if (time && fromValue && selectedProducts.size > 0 && selectedLocations.size > 0) {
      setFilters({
        version: [{ id: fromValue, name: fromValue }],
        time: [time],
        product: Array.from(selectedProducts.values()),
        location: Array.from(selectedLocations.values()),
      });
    }
  }, [
    fromValue,
    time,
    setFilters,
    selectedProducts.size,
    selectedProducts.values,
    selectedLocations.size,
    selectedLocations.values,
    selectedProducts,
    selectedLocations,
  ]);

  /* Accordion methods */
  const goForward = useGoForward(setExpanded, 4);
  const goBack = useGoBack(setExpanded);

  /* Dropdown methods */
  // typed example if your "DropdownOption" = { text: string; value: string }
  const handleMovementDropdownChange = useCallback(
    (_event: any, newValue: DropdownOption | null) => {
      if (!newValue) return;

      // no change
      if (newValue.text === planMovement) return;

      if (isNil(props.controlPoints)) {
        // can't set defaults without controlPoints
        setPlanMovement(newValue.text);
        return;
      }

      const { selectedProducts, selectedLocations, time, timeOptions } = setWizardDefaults(
        newValue /* PlanMovementOption */,
        props.controlPoints
      );

      setPlanMovement(newValue.text);
      setAllProducts(true);
      setAllLocations(true);
      setSelectedProducts(selectedProducts);
      setSelectedLocations(selectedLocations);
      setTime(time);
      setTimeOptions(timeOptions);
    },
    [props.controlPoints, planMovement]
  );

  const handleTimeDropdownChange = useCallback(
    (_event: any, newValue: DropdownOption | null) => {
      if (!newValue) return;
      // no change
      if (newValue.text === time) return;
      setTime(newValue.text);
    },
    [time]
  );

  /* Toggles methods */

  const handleProductToggle = useCallback(
    (productId: string) => {
      const nextSelection = new Set(selectedProducts);

      if (selectedProducts.has(productId)) {
        nextSelection.delete(productId);
      } else {
        nextSelection.add(productId);
      }

      // determines correct toggle state for select all checkbox
      // by retrieving selected planMovement option and parsing 'To' value
      // to lookup correct products in correct controlPoint

      const { option } = getSelectedOption(planMovement, planMovementOptions);
      const [_from, _, to] = parsePlanMovementOptionValue(option);
      const controlPoint = props.controlPoints ? props.controlPoints[to] : null;
      const selectAllProducts = isNil(controlPoint?.product)
        ? allProducts
        : nextSelection.size === controlPoint?.product.length;

      setAllProducts(selectAllProducts);
      setSelectedProducts(nextSelection);
    },
    [props.controlPoints, planMovement, planMovementOptions, selectedProducts, allProducts]
  );

  const handleLocationToggle = useCallback(
    (productId: string) => {
      const nextSelection = new Set(selectedLocations);

      if (selectedLocations.has(productId)) {
        nextSelection.delete(productId);
      } else {
        nextSelection.add(productId);
      }

      // determines correct toggle state for select all checkbox

      const { option } = getSelectedOption(planMovement, planMovementOptions);
      const [_from, _, to] = parsePlanMovementOptionValue(option);
      const controlPoint = props.controlPoints ? props.controlPoints[to] : null;
      const selectAllLocations = isNil(controlPoint?.location)
        ? allLocations
        : nextSelection.size === controlPoint?.location.length;

      setAllLocations(selectAllLocations);
      setSelectedLocations(nextSelection);
    },
    [props.controlPoints, planMovement, planMovementOptions, selectedLocations, allLocations]
  );

  const toggleAllProducts = useCallback(() => {
    const { option } = getSelectedOption(planMovement, planMovementOptions);
    const [_from, _, to] = parsePlanMovementOptionValue(option);
    const controlPoint = props.controlPoints ? props.controlPoints[to] : null;

    if (isNil(controlPoint?.product)) {
      return;
    }

    const nextSelection = new Set<string>();

    if (allProducts) {
      setAllProducts(false);
      setSelectedProducts(nextSelection);
      return;
    }

    controlPoint?.product.forEach((product) => {
      nextSelection.add(product.id);
    });

    setAllProducts(true);
    setSelectedProducts(nextSelection);
  }, [props.controlPoints, planMovement, planMovementOptions, allProducts]);

  const toggleAllLocations = useCallback(() => {
    const { option } = getSelectedOption(planMovement, planMovementOptions);
    const [_from, _, to] = parsePlanMovementOptionValue(option);
    const controlPoint = props.controlPoints ? props.controlPoints[to] : null;

    if (isNil(controlPoint?.location)) {
      return;
    }

    const nextSelection = new Set<string>();

    if (allLocations) {
      setAllLocations(false);
      setSelectedLocations(nextSelection);
      return;
    }

    controlPoint?.location.forEach((location) => {
      nextSelection.add(location.id);
    });

    setAllLocations(true);
    setSelectedLocations(nextSelection);
  }, [props.controlPoints, planMovement, planMovementOptions, allLocations]);

  /* Tab methods */

  const handleTabChange = useCallback((_event: React.ChangeEvent<{}>, value: any) => {
    setSummaryTab(value);
  }, []);

  /* Grid methods */

  const onDataRendered = useCallback((event: FirstDataRenderedEvent) => {
    event.api.sizeColumnsToFit();
    event.api.hideOverlay();
  }, []);

  const openModal = useCallback(() => {
    setModalVisible(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const getMemberNameFromId = useCallback(
    (params: ValueGetterParams) => {
      if (!params.colDef.field) {
        return;
      }
      const id = get(params.data, params.colDef.field);
      if (props.controlPoints && toValue) {
        return getMemberNameFromSpace(id, props.controlPoints[toValue]);
      }
      return id;
    },
    [props.controlPoints, toValue]
  );

  const handleSubmitMassCopy = useCallback(async () => {
    setLoading(true);
    if (selectedLocations.size > 0 && selectedProducts.size > 0 && toValue && plans) {
      const planIds = plans.map((p) => p.id);
      const clonePlamResp = await clonePlans({ client, planIds, version: toValue });

      if (clonePlamResp.type === 'workingSets/clonePlan/rejected') {
        // TODO: handle this correctly
        toast.error('An error occured copying this plan', {
          position: toast.POSITION.TOP_LEFT,
        });
        logger.error(`An error occured copying the plan ids ${planIds} to ${toValue}`);
      } else {
        toast.info('Plans Copied Successfully!');
      }

      // reset everything
      setLoading(false);
      setModalVisible(false);
      setSelectedProducts(new Set());
      setSelectedLocations(new Set());
      setPlanMovement('');
      setTime(undefined);
      setExpanded(0);
    }
  }, [client, clonePlans, logger, plans, selectedLocations.size, selectedProducts.size, toValue]);

  return (
    <div className="visualize-summary-pivot">
      <MfpSubheader title={'Mass Plan Copy'} />
      <Stepper activeStep={expanded} className="submission-wizard-sections" orientation="vertical">
        <Step expanded={expanded === WizardSection.Plan}>
          <StepLabel classes={{ root: 'summary' }}>
            <Typography variant={'h6'}>Select Plan Versions</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <div className="dropdown-group">
              <div className="dropdown-group-label">Select Plan Movement</div>
              <Autocomplete
                disableClearable
                data-qa="submission-wizard-plan-movement-dropdown"
                closeIcon={null}
                popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                options={planMovementOptions}
                getOptionLabel={(option) => option.text}
                value={planMovementOptions.find((opt) => opt.text === planMovement) || undefined}
                onChange={handleMovementDropdownChange}
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
              />
            </div>
            <WizardActions
              section={WizardSection.Plan}
              onContinue={goForward}
              onGoBack={goBack}
              disabled={planMovement === ''}
            />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.Product}>
          <StepLabel classes={{ root: 'summary' }}>
            <Typography variant={'h6'}>Select Product(s)</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <div className="toggles-section">
              <TogglesSelectAll isChecked={allProducts} onCheckBoxToggle={toggleAllProducts} />
              <div className="toggles-container">
                {products
                  ? products.map((product) => {
                      return (
                        <section className="toggle-group" key={product.id}>
                          <span className="toggle-title">{product.name}</span>
                          <div className="toggle-container">
                            <PivotToggle
                              checked={selectedProducts.has(product.id)}
                              locked={false}
                              onToggleClick={() => handleProductToggle(product.id)}
                            />
                          </div>
                        </section>
                      );
                    })
                  : 'No products found'}
              </div>
              <TogglesStatus itemCount={products?.length || 0} selectionCount={selectedProducts.size} />
            </div>
            <WizardActions
              section={WizardSection.Product}
              onContinue={goForward}
              onGoBack={goBack}
              disabled={planMovement === '' || selectedProducts.size === 0}
            />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.Location}>
          <StepLabel classes={{ root: 'summary' }}>
            <Typography variant={'h6'}>Select Location(s)</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <div className="toggles-section">
              <TogglesSelectAll isChecked={allLocations} onCheckBoxToggle={toggleAllLocations} />
              <div className="toggles-container">
                {locations
                  ? locations.map((location) => {
                      return (
                        <section className="toggle-group" key={location.id}>
                          <span className="toggle-title toggle-title-upper">
                            {!isNil(locationDisplay) ? location[locationDisplay] : location.id}
                          </span>
                          <div className="toggle-container">
                            <PivotToggle
                              checked={selectedLocations.has(location.id)}
                              locked={false}
                              onToggleClick={() => handleLocationToggle(location.id)}
                            />
                          </div>
                        </section>
                      );
                    })
                  : 'No locations found'}
              </div>
              <TogglesStatus itemCount={locations?.length || 0} selectionCount={selectedLocations.size} />
            </div>
            <WizardActions
              section={WizardSection.Location}
              onContinue={goForward}
              onGoBack={goBack}
              disabled={planMovement === '' || selectedProducts.size === 0 || selectedLocations.size === 0}
            />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.Time}>
          <StepLabel classes={{ root: 'summary' }}>
            <Typography variant={'h6'}>Select Time</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <div className="dropdown-group">
              <div className="dropdown-group-label">Select Plan Time</div>
              <Autocomplete
                disableClearable
                data-qa="submission-wizard-time-dropdown"
                closeIcon={null}
                popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                options={timeOptions}
                getOptionLabel={(option) => option.text}
                value={timeOptions.find((opt) => opt.text === time) || undefined}
                onChange={handleTimeDropdownChange}
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
              />
            </div>
            <WizardActions
              section={WizardSection.Time}
              onContinue={goForward}
              onGoBack={goBack}
              disabled={planMovement === '' || selectedProducts.size === 0 || selectedLocations.size === 0 || !time}
            />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.Summary}>
          <StepLabel classes={{ root: 'summary' }}>
            <Typography variant={'h6'}>Summary</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details-ignore-width' }}>
            <Tabs classes={{ indicator: 'selected' }} value={summaryTab} onChange={handleTabChange}>
              <Tab label={'Summary'} />
              <Tab label={'Details'} />
            </Tabs>
            <TabPanel value={summaryTab} index={0}>
              <section className="summary-container">
                <Typography className="summary-label first-label">From Version:</Typography>
                <Typography className="summary-label second-label">To Version:</Typography>
                <Typography className="summary-label third-label">Products:</Typography>
                <Typography className="summary-label fourth-label">Locations:</Typography>
                <Typography className="summary-label fifth-label">Time:</Typography>
                <Typography className="summary-details first-details">{fromText || '- no selection -'}</Typography>
                <Typography className="summary-details second-details">{toText || '- no selection -'}</Typography>
                <Typography className="summary-details third-details" noWrap={true}>
                  {Array.from(selectedProducts).join(', ')}
                </Typography>
                <Typography className="summary-details fourth-details" noWrap={true}>
                  {Array.from(selectedLocations).join(', ')}
                </Typography>
                <Typography className="summary-details fifth-details">{time}</Typography>
              </section>
            </TabPanel>
            <TabPanel value={summaryTab} index={1}>
              <section className="grid-container" style={{ height: '250px' }}>
                <AgGridReact
                  rowData={plans}
                  onFirstDataRendered={onDataRendered}
                  rowHeight={40}
                  columnDefs={[
                    {
                      field: 'space.product',
                      valueGetter: getMemberNameFromId,
                      headerName: 'Product',
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 150,
                      cellStyle: { textAlign: 'center' },
                    },
                    {
                      field: 'space.location',
                      headerName: 'Location',
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 75,
                      cellStyle: { textAlign: 'center' },
                    },
                    {
                      field: 'space.time',
                      headerName: 'Time',
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 75,
                      cellStyle: { textAlign: 'center' },
                    },
                    {
                      field: 'version',
                      headerName: 'Version',
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 75,
                      cellStyle: { textAlign: 'center' },
                    },
                    {
                      field: 'createdAt',
                      valueFormatter: formatDate,
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 150,
                      cellStyle: { textAlign: 'center' },
                    },
                    {
                      field: 'authoredBy.email',
                      suppressMovable: true,
                      suppressMenu: true,
                      width: 150,
                      cellStyle: { textAlign: 'center' },
                    },
                  ]}
                ></AgGridReact>
              </section>
            </TabPanel>
            <WizardActions
              section={WizardSection.Summary}
              onContinue={openModal}
              onGoBack={goBack}
              loading={isLoading}
              disabled={planMovement === '' || selectedProducts.size === 0 || selectedLocations.size === 0 || !time}
            />
          </StepContent>
        </Step>
      </Stepper>
      {isModalVisible && (
        <TemplateModalWithButton
          title="Mass Copy Plans"
          buttonComponent={<span style={{ display: 'none' }} />}
          openOverride={true}
          showSubmit={true}
          onSubmit={handleSubmitMassCopy}
          onCancel={closeModal}
          paperClassName="import-version-modal"
          data-qa="copy-plan-modal"
          customSubmitComponent={({ submit }) => (
            <div className="footer-container">
              <SquareActionButton
                className="button modal-button close-button"
                onClick={closeModal}
                type="secondary"
                text="Close"
              />
              <SquareActionButton
                className="button modal-button submit-modal-button"
                dataQa="copy-plan-btn-copy"
                onClick={submit}
                disabled={isLoading}
                loading={isLoading}
                text={isLoading ? 'Submitting...' : 'Submit'}
                icon="far fa-check"
                type="primary"
              />
            </div>
          )}
        >
          <Paper elevation={1} className="modal-message">
            This action will copy all <strong>{plans?.length || 0}</strong> plans of the version &quot;
            <strong>{(fromValue || '').toUpperCase()}</strong>&quot; to &quot;
            <strong>{(toValue || '').toUpperCase()}</strong>&quot;. Do you wish to proceed?
          </Paper>
        </TemplateModalWithButton>
      )}
    </div>
  );
};

export default connect<SubmissionWizardValueProps, SubmissionWizardDispatchProps, SubmissionWizardOwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps
)(SubmissionWizard);
